import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
    VStack, HStack, Text, Checkbox, Box, Flex, Button, Input, IconButton,
} from '@chakra-ui/react';
import { Plus, Trash2 } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { ViewFilter, FilterRule } from '../../core/types';

/** Categories offered in the Visibility/Graphics dialog. */
const VG_CATEGORIES = [
    'Walls', 'Doors', 'Windows', 'Floors', 'Roofs', 'Ceilings',
    'Structural Columns', 'Structural Framing', 'Stairs', 'Railings',
    'Furniture', 'Rooms', 'Areas', 'Grids', 'Levels',
    'Ducts', 'Pipes', 'Conduits', 'Cable Trays',
    'Mechanical Equipment', 'Electrical Equipment', 'Plumbing Fixtures',
    'Lighting Fixtures', 'Dimensions', 'Text Notes', 'Tags',
    'Generic Annotations', 'Detail Items', 'Revision Clouds', 'Views',
    'Precast Walls', 'Precast Floors', 'Precast Columns', 'Precast Beams',
    'Precast Mounting Parts', 'Precast Reinforcement',
];

/**
 * Visibility/Graphics dialog (BIM VG): per-category visibility checkboxes
 * and a simple filters list persisted on the active view. The renderer gates
 * elements through passesViewScope() in BIMSceneContent.
 */
export function VisibilityGraphicsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'visibilityGraphics');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const activeView = useBIMStore((s) => s.project.views.get(s.project.activeViewId));
    const updateView = useBIMStore((s) => s.updateView);

    const [onlyChecked, setOnlyChecked] = useState(false);
    const visibility = activeView?.visibility ?? {};
    const filters = useMemo<ViewFilter[]>(() => activeView?.filters ?? [], [activeView?.filters]);

    const setCategoryVisible = (category: string, visible: boolean) => {
        if (!activeView) return;
        updateView(activeView.id, {
            visibility: { ...activeView.visibility, [category]: visible },
        });
    };

    const addFilter = () => {
        if (!activeView) return;
        const filter: ViewFilter = {
            id: `vf-${Date.now()}`,
            name: `Filter ${filters.length + 1}`,
            categories: [VG_CATEGORIES[0]],
            rules: [{ parameter: 'Type', evaluator: 'contains', value: '' }],
        };
        updateView(activeView.id, { filters: [...filters, filter] });
    };

    const removeFilter = (id: string) => {
        if (!activeView) return;
        updateView(activeView.id, { filters: filters.filter(f => f.id !== id) });
    };

    const updateFilterField = (id: string, updates: Partial<ViewFilter>) => {
        if (!activeView) return;
        updateView(activeView.id, {
            filters: filters.map(f => (f.id === id ? { ...f, ...updates } : f)),
        });
    };

    const updateRule = (filterId: string, index: number, rule: Partial<FilterRule>) => {
        const f = filters.find(x => x.id === filterId);
        if (!f || !activeView) return;
        const rules = f.rules.map((r, i) => (i === index ? { ...r, ...rule } : r));
        updateFilterField(filterId, { rules });
    };

    const shown = onlyChecked ? VG_CATEGORIES.filter(c =>visibility[c] !== false) : VG_CATEGORIES; return (<ManageDialogFrame
            title={`Visibility / Graphics${activeView ? ` — ${activeView.name}` : ''}`}
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack align="stretch" spacing={4}>
                {/* Categories */}
                <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>{t('manage.categories')}</Text>
                    <HStack spacing={2} mb={2}>
                        <Checkbox size="sm" isChecked={onlyChecked} onChange={(e) => setOnlyChecked(e.target.checked)}>
                            <Text fontSize="xs">{t('manage.showCheckedOnly')}</Text>
                        </Checkbox>
                    </HStack>
                    <Box maxH="240px" overflowY="auto" borderWidth="1px" borderColor="border.base" borderRadius="md" p={2}>
                        <Flex wrap="wrap">
                            {shown.map((category) => (
                                <Box key={category} w="33%">
                                    <Checkbox
                                        size="sm"
                                        mb={1}
                                        isChecked={visibility[category] !== false}
                                        onChange={(e) => setCategoryVisible(category, e.target.checked)}
                                    >
                                        <Text fontSize="xs">{category}</Text>
                                    </Checkbox>
                                </Box>
                            ))}
                        </Flex>
                    </Box>
                </Box>

                {/* Filters */}
                <Box>
                    <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm" fontWeight="bold">{t('manage.filters')}</Text>
                        <Button size="xs" leftIcon={<Plus size={12} />} onClick={addFilter}>{t('manage.add')}</Button>
                    </HStack>
                    {filters.length === 0 && (
                        <Text fontSize="xs" color="text.secondary">{t('manage.noFiltersOnThisViewElementsMatchingAFilterSRulesStayVisible')}</Text>
                    )}
                    <VStack align="stretch" spacing={2}>
                        {filters.map((filter) => (
                            <Box key={filter.id} borderWidth="1px" borderColor="border.base" borderRadius="md" p={2}>
                                <HStack>
                                    <Input
                                        size="xs"
                                        flex="1"
                                        value={filter.name}
                                        onChange={(e) => updateFilterField(filter.id, { name: e.target.value })}
                                    />
                                    <IconButton
                                        aria-label={t('manage.deleteFilter')}
                                        size="xs"
                                        variant="ghost"
                                        icon={<Trash2 size={12} />}
                                        onClick={() => removeFilter(filter.id)}
                                    />
                                </HStack>
                                <VStack align="stretch" spacing={1} mt={2}>
                                    {filter.rules.map((rule, i) => (
                                        <HStack key={i} spacing={2}>
                                            <Input
                                                size="xs"
                                                placeholder={t('manage.parameter')}
                                                value={rule.parameter}
                                                onChange={(e) => updateRule(filter.id, i, { parameter: e.target.value })}
                                            />
                                            <select
                                                className="filter-rule-evaluator"
                                                value={rule.evaluator}
                                                onChange={(e) => updateRule(filter.id, i, { evaluator: e.target.value as FilterRule['evaluator'] })}
                                            >
                                                {(['equals', 'greater', 'less', 'contains', 'beginsWith'] as const).map(op => (
                                                    <option key={op} value={op}>{op}</option>
                                                ))}
                                            </select>
                                            <Input
                                                size="xs"
                                                placeholder={t('manage.value')}
                                                value={String(rule.value)}
                                                onChange={(e) => updateRule(filter.id, i, { value: e.target.value })}
                                            />
                                            <IconButton
                                                aria-label={t('manage.removeRule')}
                                                size="xs"
                                                variant="ghost"
                                                icon={<Trash2 size={11} />}
                                                onClick={() => updateFilterField(filter.id, { rules: filter.rules.filter((_, x) => x !== i) })}
                                            />
                                        </HStack>
                                    ))}
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        w="fit-content"
                                        leftIcon={<Plus size={11} />}
                                        onClick={() => updateFilterField(filter.id, {
                                            rules: [...filter.rules, { parameter: '', evaluator: 'equals', value: '' }],
                                        })}
                                    >{t('manage.addRule')}</Button>
                                </VStack>
                            </Box>
                        ))}
                    </VStack>
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
